/**
 * F3 — Client-side body cache for NIPE-proposed SharePoint writes.
 *
 * Bodies are kept in-memory only (not persisted to Convex) so a 80kb
 * draft never touches a 1MB row, never bleeds across users, and is
 * dropped on tab close. The pending-write row stores a sha256 hash so
 * the tray can verify the body matches at approval time.
 *
 * The cache is keyed by Convex row id (string). Approval reads the body
 * out, performs the Graph put, and clears the entry on completion.
 *
 * @see {@link ../writes/lanes.md} — this module is **lane C** in the
 * writes-routing vocabulary (human-approval gated). Approval handlers
 * call into lane B (`vault/saveProvider.ts`). Other lane: A
 * (`edits/localEditQueue.ts`).
 */

const bodies = new Map<string, string>();

/**
 * B.6 — Sidecar cache for auto-approve attribution. When a write was
 * auto-approved by a nipeApprovalRule, the SPA stores `{ruleId, label}`
 * here so the tray's approval handler can pass them to the
 * SharePoint audit log entry (B.6 field `autoApprovedByRuleId`).
 *
 * Same lifecycle as the body cache: in-memory only, dropped on tab
 * close. Auto-approve attribution that needs to survive a refresh
 * lives on the durable Convex audit row (not here).
 */
const autoApproveAttribution = new Map<
  string,
  { ruleId: string; ruleLabel: string }
>();

export function setPendingWriteBody(pendingWriteId: string, body: string): void {
  bodies.set(pendingWriteId, body);
}

export function getPendingWriteBody(pendingWriteId: string): string | null {
  return bodies.get(pendingWriteId) ?? null;
}

export function clearPendingWriteBody(pendingWriteId: string): void {
  bodies.delete(pendingWriteId);
  autoApproveAttribution.delete(pendingWriteId);
}

export function listCachedPendingWriteIds(): string[] {
  return Array.from(bodies.keys());
}

/** B.6 — Record that a pending write was auto-approved by a rule. */
export function setAutoApproveAttribution(
  pendingWriteId: string,
  attribution: { ruleId: string; ruleLabel: string },
): void {
  autoApproveAttribution.set(pendingWriteId, attribution);
}

/**
 * B.6 — Read back the auto-approve attribution for a pending write.
 * Returns null if the write was manually approved (no rule matched).
 */
export function getAutoApproveAttribution(
  pendingWriteId: string,
): { ruleId: string; ruleLabel: string } | null {
  return autoApproveAttribution.get(pendingWriteId) ?? null;
}

/**
 * SHA-256 hex of a string body. Uses the WebCrypto API; throws if the
 * subtle crypto is unavailable (Node without `globalThis.crypto`).
 */
export async function hashBody(body: string): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new Error('WebCrypto subtle API unavailable');
  const bytes = new TextEncoder().encode(body);
  const digest = await subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Trim a body to the preview length stored on the pending-write row. */
export function bodyPreview(body: string, max = 200): string {
  if (body.length <= max) return body;
  return body.slice(0, max - 1) + '…';
}
